import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { TrashIcon } from "../Icons";
import { CustomSelect } from "@components/UI";
import { PaginationEllipsis } from "@components/Pagination";

function TableForProductManagement({
  columns,
  data,
  scrollableBody = false,
  colSize = 12,
  maxPagesToShow = 2,
  onRowClick,
  maxRowsPerPage = 10,
  currentPage,
  setCurrentPage,
  loading = false,
  totalPages,
  totalData,
  columnConfig = {},
  hasCheckbox = false,
  hasRadioButton = false,
  hasDeleteColumn = false, // Add prop to show Delete column
  showCheckboxOnlyWhenSelected = false,
  onCheckboxChange,
  onDeleteRow, // Add prop to handle delete action
  showPagination = true,
  selectedRows = [],
  disabledRow = [],
  onItemsPerPageChange,
  rowKey = "seq",
  shouldShowGoToPage = colSize === 6 || colSize === 8 || colSize === 12,
  tableStyle = "default",
  containerStyle = {},
  rowHeight = null,
  stickyHeader = false,
  scrollY = null,
  isCheckboxDisabled = null, // Add prop to disable specific checkboxes
  isDeleteDisabled = null, // Add prop to disable specific delete buttons
  paddingYHeader = "py-4",
  customRowRender = null, // Prop for custom row rendering
  resizable = true, // Add prop to enable/disable column resizing
  dataTestId,
}) {

  const { t, i18n } = useTranslation();
  const { colors } = useTheme();

  const tableTestId = dataTestId || "table-for-product-management";
  const headerTestId = (suffix) => dataTestId ? `${dataTestId}-header-${suffix}` : `header-${suffix}`;
  const noDataTestId = dataTestId ? `${dataTestId}-no-data-cell` : "no-data-cell";

  const [showSpinner, setShowSpinner] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(maxRowsPerPage);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    let showTimeout;
    let hideTimeout;

    if (loading) {
      showTimeout = setTimeout(() => setShowSpinner(true), 100);
    } else {
      hideTimeout = setTimeout(() => setShowSpinner(false), 100);
    }

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [loading]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const commitPage = () => {
    if (pageInput === "") {
      setCurrentPage(1);
      setPageInput("1");
      return;
    }

    let page = Number(pageInput);

    if (!Number.isInteger(page)) {
      setPageInput(String(currentPage));
      return;
    }

    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;

    setCurrentPage(page);
    setPageInput(String(page));
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleFirst = () => {
    setCurrentPage(1);
  };

  const handleLast = () => {
    setCurrentPage(totalPages);
  };

  const getPaginationRange = () => {
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const delta = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, currentPage - delta);
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }

    if (startPage > 1) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      pages.push("...");
    }

    return pages;
  };

  const handleRowCheckboxChange = (row) => {
    if (!onCheckboxChange) return;

    if (typeof rowKey === "string") {
      const rowValue = row[rowKey];
      const isSelected = selectedRows.includes(rowValue);
      const newSelectedRows = isSelected
        ? selectedRows.filter((value) => value !== rowValue)
        : [...selectedRows, rowValue];
      onCheckboxChange(newSelectedRows);
    } else {
      const rowObj = rowKey.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {});
      const isSelected = selectedRows.some((selectedRow) =>
        Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]),
      );
      const newSelectedRows = isSelected
        ? selectedRows.filter((selectedRow) => !Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]))
        : [...selectedRows, rowObj];
      onCheckboxChange(newSelectedRows);
    }
  };

  const handleRowRadioButtonChange = (row) => {
    if (!onCheckboxChange) return;

    if (typeof rowKey === "string") {
      const value = row[rowKey];
      onCheckboxChange([value]);
    } else {
      onCheckboxChange([row]);
    }
  };

  const handleSelectAllChange = () => {
    if (!onCheckboxChange) return;

    // Filter out rows with disabled checkboxes
    const selectableRows = data.filter(row => !(isCheckboxDisabled && isCheckboxDisabled(row)));

    if (typeof rowKey === "string") {
      const allValues = selectableRows.map((row) => row[rowKey]);
      const allSelected = allValues.every((value) => selectedRows.includes(value));
      const newSelectedRows = allSelected ? [] : allValues;
      onCheckboxChange(newSelectedRows);
    } else {
      const allRowObjects = selectableRows.map((row) => rowKey.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {}));
      const allSelected = allRowObjects.every((rowObj) =>
        selectedRows.some((selectedRow) => Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key])),
      );
      const newSelectedRows = allSelected ? [] : allRowObjects;
      onCheckboxChange(newSelectedRows);
    }
  };

  const handleItemsPerPageChange = (selectedOption) => {
    const value = parseInt(selectedOption.value);
    setItemsPerPage(value);
    onItemsPerPageChange(value);
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const th = e.target.closest('th');
    if (!th) return;
    
    const actualWidth = th.offsetWidth;
    const table = th.closest('table');
    if (!table) return;
    
    // Find the scrollable container and check scrollbar state once at the start
    const scrollContainer = table.closest('.overflow-x-auto') || table.parentElement;
    const hasScrollbar = scrollContainer && 
      scrollContainer.scrollWidth > scrollContainer.clientWidth;
    
    const allThs = Array.from(table.querySelectorAll('thead th'));
    const allTds = Array.from(table.querySelectorAll(`tbody td:nth-child(${columnIndex + 1 + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0)})`));
    
    const dataColumnOffset = (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0);
    const dataColumnIndex = columnIndex + dataColumnOffset;
    
    const allColumnWidths = [];
    const allColumnThs = [];
    const allColumnTds = [];
    
    for (let i = 0; i < columns.length; i++) {
      const thIndex = i + dataColumnOffset;
      if (thIndex < allThs.length) {
        const th = allThs[thIndex];
        const width = columnWidths[i] || th.offsetWidth;
        allColumnWidths.push(width);
        allColumnThs.push(th);
        const colTds = Array.from(table.querySelectorAll(`tbody td:nth-child(${thIndex + 1})`));
        allColumnTds.push(colTds);
      }
    }
    
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    
    table.style.transition = 'none';
    table.classList.add('resizing');
    
    const resizeData = {
      index: columnIndex,
      startX: e.clientX,
      startWidth: actualWidth,
      th: th,
      table: table,
      scrollContainer: scrollContainer,
      hasScrollbar: hasScrollbar,
      nextTh: columnIndex < columns.length - 1 ? allThs[dataColumnIndex + 1] : null,
      allTds: allTds,
      nextTds: columnIndex < columns.length - 1 
        ? Array.from(table.querySelectorAll(`tbody td:nth-child(${dataColumnIndex + 2})`))
        : [],
      nextColumnWidth: columnIndex < columns.length - 1 
        ? allColumnWidths[columnIndex + 1]
        : null,
      allColumnWidths: allColumnWidths,
      allColumnThs: allColumnThs,
      allColumnTds: allColumnTds,
    };
    
    setResizingColumn(resizeData);
  };

  const handleResizeMove = (e) => {
    if (!resizingColumn) return;
    
    e.preventDefault();
    
    const diff = e.clientX - resizingColumn.startX;
    const isReducing = diff < 0;
    
    const newWidth = Math.max(50, resizingColumn.startWidth + diff);
    const widthDiff = newWidth - resizingColumn.startWidth;
    
    for (let i = 0; i < resizingColumn.allColumnThs.length; i++) {
      let widthToApply;
      
      if (i === resizingColumn.index) {
        // Current column being resized
        widthToApply = newWidth;
      } else if (i < resizingColumn.index) {
        // Previous columns - keep their widths unchanged
        widthToApply = resizingColumn.allColumnWidths[i];
      } else {
        // Following columns
        if (isReducing) {
          if (resizingColumn.hasScrollbar) {
            // When reducing with scrollbar: keep following columns' widths unchanged (they shift left, table shrinks)
            widthToApply = resizingColumn.allColumnWidths[i];
          } else {
            // When reducing without scrollbar: expand following columns to fill the space
            const followingColumnsCount = resizingColumn.allColumnThs.length - resizingColumn.index - 1;
            if (followingColumnsCount > 0) {
              // Distribute the space (negative widthDiff) among all following columns
              const spacePerColumn = Math.abs(widthDiff) / followingColumnsCount;
              widthToApply = resizingColumn.allColumnWidths[i] + spacePerColumn;
              widthToApply = Math.max(20, widthToApply); // Minimum width
            } else {
              widthToApply = resizingColumn.allColumnWidths[i];
            }
          }
        } else {
          // When expanding: keep following columns' widths unchanged (they shift right)
          widthToApply = resizingColumn.allColumnWidths[i];
        }
      }
      
      if (resizingColumn.allColumnThs[i]) {
        resizingColumn.allColumnThs[i].style.width = `${widthToApply}px`;
      }
      
      if (resizingColumn.allColumnTds[i]) {
        resizingColumn.allColumnTds[i].forEach(td => {
          td.style.width = `${widthToApply}px`;
        });
      }
    }
  };

  const handleResizeEnd = () => {
    if (!resizingColumn) return;
    
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    
    if (resizingColumn.table) {
      resizingColumn.table.style.transition = '';
      resizingColumn.table.classList.remove('resizing');
    }
    
    const finalWidths = {};
    for (let i = 0; i < resizingColumn.allColumnThs.length; i++) {
      if (resizingColumn.allColumnThs[i]) {
        finalWidths[i] = resizingColumn.allColumnThs[i].offsetWidth;
      }
    }
    
    setColumnWidths((prev) => {
      return {
        ...prev,
        ...finalWidths,
      };
    });
    
    setResizingColumn(null);
  };

  useEffect(() => {
    if (resizingColumn) {
      const handleMove = (e) => {
        requestAnimationFrame(() => handleResizeMove(e));
      };
      
      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleResizeEnd, { once: true });
      document.addEventListener('mouseleave', handleResizeEnd, { once: true });

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.removeEventListener('mouseleave', handleResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        if (resizingColumn?.table) {
          resizingColumn.table.style.transition = '';
          resizingColumn.table.classList.remove('resizing');
        }
      };
    }
  }, [resizingColumn]);

  const isRowSelected = (row) => {
    if (!selectedRows || selectedRows.length === 0) return false;
    if (!rowKey) return false;

    if (typeof rowKey === "string") {
      const rowValue = row[rowKey];
      if (selectedRows.length > 0 && typeof selectedRows[0] === "object") {
        return selectedRows.some((selectedRow) => selectedRow[rowKey] === rowValue);
      } else {
        return selectedRows.includes(rowValue);
      }
    }

    if (Array.isArray(rowKey)) {
      return selectedRows.some((selectedRow) => {
        if (typeof selectedRow === "object") {
          return rowKey.every((key) => selectedRow[key] === row[key]);
        }
        return false;
      });
    }

    return false;
  };

  const isRowDisabled = (row) => {
    if (!disabledRow) return false;

    const disabledRowsArray = Array.isArray(disabledRow) ? disabledRow : [disabledRow];

    return disabledRowsArray.some((disabled) => {
      if (Array.isArray(rowKey)) {
        return rowKey.every((key) => row[key] === disabled[key]);
      } else {
        return row[rowKey] === disabled[rowKey];
      }
    });
  };

  const getRowStripeColor = (index) => {
    return index % 2 === 0 ? colors.bgTertiary : colors.bgPrimary;
  };

  if (scrollableBody) {
    return (
      <div style={containerStyle} className={`flex flex-col relative flex-1 min-h-0`} data-testid={tableTestId}>
        {/* Loading Overlay */}
        {showSpinner && (
          <div className={`bg-opacity-50 absolute inset-0 z-10 ${colors.bgPrimary}`} data-testid={`${tableTestId}-loading`}>
            <div className="p-4">
              {/* Skeleton Header */}
              <div className="mb-5">
                <div className="flex space-x-4">
                  {Array.from({ length: columns.length }).map((_, index) => (
                    <div key={index} className="flex-1">
                      <div className={`h-6 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skeleton Rows */}
              {Array.from({ length: Math.min(5, data.length || 5) }).map((_, rowIndex) => (
                <div key={rowIndex} className="mb-4">
                  <div className="flex space-x-4">
                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                      <div key={colIndex} className="flex-1">
                        <div className={`h-5 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          className={`flex-1 overflow-auto min-h-0 border rounded-lg ${colors.borderPrimary}`}
          data-testid={`${tableTestId}-table-wrapper`}
        >
          {/* Table */}
          <table
            className={`w-full ${tableStyle === "default" ? "table-fixed" : tableStyle}`}
            data-testid={`${tableTestId}-table`}
          >
            <thead
              className={`${colors.bgPrimary} ${stickyHeader ? "sticky top-0 z-20" : ""}`}
              data-testid={`${tableTestId}-table-head`}
            >
              <tr className='bg-inherit'>
                {hasCheckbox && !showCheckboxOnlyWhenSelected && (
                  <th className={`w-16 px-4 ${paddingYHeader} text-left`} title="Select All" data-testid={headerTestId("checkbox")}>
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={data.length > 0 && data.filter(row => !(isCheckboxDisabled && isCheckboxDisabled(row))).every((row) => isRowSelected(row))}
                        onChange={handleSelectAllChange}
                      />
                      <div
                        className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${data.length > 0 && data.every((row) => isRowSelected(row))
                          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                          }`}
                        onClick={handleSelectAllChange}
                      >
                        {data.length > 0 && data.every((row) => isRowSelected(row)) && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                )}
                {hasCheckbox && showCheckboxOnlyWhenSelected && (
                  <th className={`w-8 ${paddingYHeader}`} data-testid={headerTestId("checkbox-placeholder")}>
                  </th>
                )}
                {hasRadioButton && <th className={`w-16 px-4 ${paddingYHeader}`} title="Select" data-testid={headerTestId("radio")}></th>}
                {columns.map((column, index) => {
                  const columnKey = column;
                  const config = columnConfig[columnKey] || {};
                  const { headerRender, title, hideBorder } = config;
                  const columnTitle = title || column;

                  return (
                    <th
                      key={index}
                      data-testid={headerTestId(columnKey)}
                      className={`${colors.textSubTitle} px-4 ${paddingYHeader} text-left text-xs font-semibold tracking-wider overflow-hidden uppercase relative`}
                      title={!headerRender ? columnTitle : undefined}
                      style={{
                        ...config.style,
                        whiteSpace: config.style?.whiteSpace || "normal",
                        textOverflow: "ellipsis",
                        width: columnWidths[index] ? `${columnWidths[index]}px` : config.style?.width || 'auto',
                      }}
                    >
                      {headerRender ? (
                        headerRender(columnKey)
                      ) : (
                        <div className="truncate py-1" title={columnTitle}>
                          {columnTitle}
                        </div>
                      )}
                      {resizable && (
                        <div
                          className={`absolute right-0 top-0 h-full w-1 cursor-col-resize`}
                          onMouseDown={(e) => handleResizeStart(e, index)}
                          style={{ userSelect: 'none' }}
                        >
                          {!hideBorder && (index < columns.length - 1 || (index === columns.length - 1 && hasDeleteColumn)) && (
                            <div
                              className={`h-full border-r ${colors.borderPrimary}`}
                              style={{
                                height: 'calc(100% - 16px)',
                                margin: '8px 0px',
                              }}
                            ></div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
                {hasDeleteColumn && (
                  <th
                    className={`${colors.textSubTitle} w-20 p-4 text-center text-xs font-semibold tracking-wider whitespace-nowrap uppercase`}
                    data-testid={headerTestId("action")}
                  >
                    {t("common.action")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className={colors.bgTableBody} data-testid={`${tableTestId}-table-body`}>
              {!loading && data.length === 0 && (
                <tr data-testid={`${tableTestId}-empty-row`}>
                  <td
                    className={`px-4 py-8 text-center ${colors.textMuted}`}
                    data-testid={noDataTestId}
                    colSpan={
                      columns.length + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0) + (hasDeleteColumn ? 1 : 0)
                    }
                  >
                    {t("common.no-data-to-display")}
                  </td>
                </tr>
              )}
              {data &&
                data.length > 0 &&
                data.map((row, index) => {
                  // If customRowRender is provided, use it
                  if (customRowRender) {
                    return customRowRender(row, index, (
                      <>
                        {hasCheckbox && (
                          <td className={`${showCheckboxOnlyWhenSelected ? 'px-0' : 'px-4'} py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                {!showCheckboxOnlyWhenSelected || isRowSelected(row) ? (
                                  <>
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={isRowSelected(row)}
                                      disabled={isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))}
                                      onChange={() => handleRowCheckboxChange(row)}
                                    />
                                    <div
                                      className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))
                                        ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgDisabledControl} opacity-50`
                                        : isRowSelected(row)
                                          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                        }`}
                                      onClick={() => !isRowDisabled(row) && !(isCheckboxDisabled && isCheckboxDisabled(row)) && handleRowCheckboxChange(row)}
                                    >
                                      {isRowSelected(row) && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="h-4 w-4"></div>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        {hasRadioButton && (
                          <td className={`px-4 py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                <input
                                  type="radio"
                                  className="sr-only"
                                  checked={isRowSelected(row)}
                                  disabled={isRowDisabled(row)}
                                  onChange={() => handleRowRadioButtonChange(row)}
                                />
                                <div
                                  className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                    ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                    : isRowSelected(row)
                                      ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                      : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                    }`}
                                  onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                                >
                                  {isRowSelected(row) && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className={`h-2 w-2 rounded-full ${colors.bgActivePrimary}`}></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          const columnKey = column;
                          const config = columnConfig[columnKey] || {};
                          const { customRender } = config;

                          return (
                            <td
                              key={colIndex}
                              className={`px-4 py-3 text-sm ${colors.textPrimary} overflow-hidden truncate`}
                              title={
                                typeof row[columnKey] === "string" || typeof row[columnKey] === "number"
                                  ? row[columnKey]
                                  : undefined
                              }
                              style={{
                                ...config.style,
                                whiteSpace: config.style?.whiteSpace || "nowrap",
                                textOverflow: "ellipsis",
                                ...(rowHeight && { height: `${rowHeight}px` }),
                                width: columnWidths[colIndex] ? `${columnWidths[colIndex]}px` : config.style?.width || 'auto',
                              }}
                            >
                              {customRender ? (
                                <div className="truncate">{customRender(row[columnKey], row, columnKey, index)}</div>
                              ) : (
                                <div className="truncate">{row[columnKey]}</div>
                              )}
                            </td>
                          );
                        })}
                        {hasDeleteColumn && (
                          <td className="flex items-center justify-center px-4 py-3">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                  onDeleteRow(row);
                                }
                              }}
                              disabled={isDeleteDisabled?.(row)}
                              className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                                ? `${colors.textDisabled} cursor-not-allowed`
                                : `${colors.iconDelete} ${colors.hoverTextDanger}`
                                }`}
                              title={t("common.delete")}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </>
                    ));
                  }
                  
                  // Default row rendering
                  return (
                    <tr
                      key={index}
                      data-testid={`${tableTestId}-row-${index}`}
                      className={`${getRowStripeColor(index)} ${isRowSelected(row) ? `!${colors.bgSelectedRow}` : ""} ${isRowDisabled(row) ? "cursor-not-allowed opacity-50" : ""} ${onRowClick && !isRowDisabled(row) ? `cursor-pointer transition-colors ${colors.hoverTableRow}` : ""} ${showCheckboxOnlyWhenSelected && (isCheckboxDisabled && isCheckboxDisabled(row)) ? "" : (isCheckboxDisabled && isCheckboxDisabled(row)) ? "opacity-60" : ""}`}
                      onClick={onRowClick && !isRowDisabled(row) ? () => onRowClick(row, index) : undefined}
                    >
                      {hasCheckbox && (
                        <td className={`${showCheckboxOnlyWhenSelected ? 'px-0' : 'px-4'} py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                          <div onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-center">
                              {!showCheckboxOnlyWhenSelected || isRowSelected(row) ? (
                                <>
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={isRowSelected(row)}
                                    disabled={isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))}
                                    onChange={() => handleRowCheckboxChange(row)}
                                  />
                                  <div
                                    className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))
                                      ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgDisabledControl} opacity-50`
                                      : isRowSelected(row)
                                        ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                        : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                      }`}
                                    onClick={() => !isRowDisabled(row) && !(isCheckboxDisabled && isCheckboxDisabled(row)) && handleRowCheckboxChange(row)}
                                  >
                                    {isRowSelected(row) && (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="h-4 w-4"></div>
                              )}
                            </div>
                          </div>
                        </td>
                      )}
                      {hasRadioButton && (
                        <td className={`px-4 py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                          <div onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-center">
                              <input
                                type="radio"
                                className="sr-only"
                                checked={isRowSelected(row)}
                                disabled={isRowDisabled(row)}
                                onChange={() => handleRowRadioButtonChange(row)}
                              />
                              <div
                                className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                  ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                  : isRowSelected(row)
                                    ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                    : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                  }`}
                                onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                              >
                                {isRowSelected(row) && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className={`h-2 w-2 rounded-full ${colors.bgActivePrimary}`}></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      {columns.map((column, colIndex) => {
                        const columnKey = column;
                        const config = columnConfig[columnKey] || {};
                        const { customRender } = config;

                        return (
                          <td
                            key={colIndex}
                            className={`px-4 py-3 text-sm ${colors.textPrimary} overflow-hidden truncate`}
                            title={
                              typeof row[columnKey] === "string" || typeof row[columnKey] === "number"
                                ? row[columnKey]
                                : undefined
                            }
                            style={{
                              ...config.style,
                              whiteSpace: config.style?.whiteSpace || "nowrap",
                              textOverflow: "ellipsis",
                              ...(rowHeight && { height: `${rowHeight}px` }),
                            }}
                          >
                            {customRender ? (
                              <div className="truncate">{customRender(row[columnKey], row, columnKey, index)}</div>
                            ) : (
                              <div className="truncate">{row[columnKey]}</div>
                            )}
                          </td>
                        );
                      })}
                      {hasDeleteColumn && (
                        <td className="flex items-center justify-center px-4 py-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                onDeleteRow(row);
                              }
                            }}
                            disabled={isDeleteDisabled?.(row)}
                            className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                              ? `${colors.textDisabledControl} cursor-not-allowed`
                              : `${colors.iconDelete} ${colors.hoverTextDanger}`
                              }`}
                            title={t("common.delete")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {showPagination && data?.length > 0 &&(
          <div className="pt-3 flex-shrink-0" data-testid={`${tableTestId}-pagination`}>
            <div className="flex items-center justify-between gap-4 flex-row">
              {/* Go to page */}
              {shouldShowGoToPage && (
                <div className="flex items-center gap-2 text-sm" data-testid={`${tableTestId}-pagination-controls`}>
                  <span className={`${colors.textSubTitle} text-xs font-medium`}>{t("common.table.go-to-page")}</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInput}
                    data-testid={`${tableTestId}-pagination-go-to-page`}
                    onChange={(e) => {
                      setPageInput(e.target.value);
                    }}
                    onBlur={commitPage}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitPage();
                        e.target.blur();
                      }
                    }}
                    className={`h-8 w-16 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
                  />
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2 text-sm" data-testid={`${tableTestId}-pagination-items-per-page`}>
                    <span className={`${colors.textSubTitle} text-xs font-medium`}>{t("common.table.items-per-page")}</span>
                    <CustomSelect
                      className="w-max"
                      data-testid={`${tableTestId}-pagination-items-per-page-select`}
                      value={{ value: itemsPerPage, label: itemsPerPage.toString() }}
                      onChange={(selectedOption) => {
                        const value = parseInt(selectedOption.value, 10);
                        setItemsPerPage(value);
                        onItemsPerPageChange(value);
                      }}
                      options={[
                        { value: 10, label: "10" },
                        { value: 20, label: "20" },
                        { value: 50, label: "50" },
                      ]}
                      size="sm"
                      isSearchable={false}
                      clearable={false}
                      customStyles={{
                        menu: (base) => ({
                          ...base,
                          width: "60px",
                        })
                      }}
                    />
                  </div>

                  {/* Showing info */}
                  <div className="flex items-center text-sm">
                    <span className={`${colors.textSubTitle} text-xs font-medium`}>
                      {(() => {
                        const startItem = ((currentPage - 1) * itemsPerPage) + 1;
                        const endItem = Math.min(currentPage * itemsPerPage, totalData);
                        const currentLanguage = i18n.language || 'ko';

                        if (currentLanguage === 'ko') {
                          return (
                            <span>
                              총 <span className={colors.textPrimary}>{totalData}</span>개 중 <span className={colors.textPrimary}>{startItem} - {endItem}</span>개 표시 중
                            </span>
                          );
                        } else {
                          return (
                            <span>
                              {t("common.table.showing")} <span className={colors.textPrimary}>{startItem} - {endItem}</span> {t("common.table.of")} <span className={colors.textPrimary}>{totalData}</span>
                            </span>
                          );
                        }
                      })()}
                    </span>
                  </div>
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center gap-1" data-testid={`${tableTestId}-pagination-pages`}>
                  {/* Previous Page Button */}
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    data-testid={`${tableTestId}-pagination-prev`}
                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
                    title="Previous page"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.prev")}</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((page, index) => (
                      <div key={index}>
                        {page === "..." ? (
                          <PaginationEllipsis
                            className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`}
                          />
                        ) : (
                          <button
                            type="button"
                            data-testid={`${tableTestId}-pagination-page-${page}`}
                            onClick={() => setCurrentPage(page)}
                            className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${page === currentPage ? `${colors.bgPaginationButtonActive}` : `${colors.bgInput}`
                              }`}
                          >
                            {page}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Next Page Button */}
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    data-testid={`${tableTestId}-pagination-next`}
                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
                    title="Next page"
                  >
                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.next")}</span>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="relative" data-testid={tableTestId}>
        <div
          style={containerStyle}
          className={`overflow-hidden rounded-lg border ${colors.borderPrimary}`}
        >
          <div
            className={`overflow-x-auto${scrollY ? " overflow-y-auto" : ""}`}
            style={scrollY ? { maxHeight: scrollY } : {}}
          >
            {/* Loading Overlay */}
            {showSpinner && (
              <div className={`bg-opacity-50 absolute inset-0 z-10 ${colors.bgPrimary}`} data-testid={`${tableTestId}-loading`}>
                <div className="p-4">
                  {/* Skeleton Header */}
                  <div className="mb-5">
                    <div className="flex space-x-4">
                      {Array.from({ length: columns.length }).map((_, index) => (
                        <div key={index} className="flex-1">
                          <div className={`h-6 ${colors.bgSkeletonDark} rounded animate-pulse`}></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skeleton Rows */}
                  {Array.from({ length: Math.min(5, data.length || 5) }).map((_, rowIndex) => (
                    <div key={rowIndex} className="mb-4">
                      <div className="flex space-x-4">
                        {Array.from({ length: columns.length }).map((_, colIndex) => (
                          <div key={colIndex} className="flex-1">
                            <div className={`h-5 ${colors.bgSkeletonDark} rounded animate-pulse`}></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Table */}
            <table
              className={`w-full ${tableStyle === "default" ? "table-fixed" : tableStyle}`}
              data-testid={`${tableTestId}-table`}
            >
              <thead
                className={`${colors.bgPrimary} ${stickyHeader ? "sticky top-0 z-20" : ""}`}
                data-testid={`${tableTestId}-table-head`}
              >
                <tr className='bg-inherit'>
                  {hasCheckbox && !showCheckboxOnlyWhenSelected && (
                    <th className={`w-16 px-4 ${paddingYHeader} text-left`} title="Select All" data-testid={headerTestId("checkbox")}>
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={data.length > 0 && data.filter(row => !(isCheckboxDisabled && isCheckboxDisabled(row))).every((row) => isRowSelected(row))}
                          onChange={handleSelectAllChange}
                        />
                        <div
                          className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${data.length > 0 && data.every((row) => isRowSelected(row))
                            ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                            : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                            }`}
                          onClick={handleSelectAllChange}
                        >
                          {data.length > 0 && data.every((row) => isRowSelected(row)) && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                  )}
                  {hasCheckbox && showCheckboxOnlyWhenSelected && (
                    <th className={`w-8 ${paddingYHeader} justify-end`} data-testid={headerTestId("checkbox-placeholder")}>
                    </th>
                  )}
                  {hasRadioButton && <th className={`w-16 px-4 ${paddingYHeader}`} title="Select" data-testid={headerTestId("radio")}></th>}
                  {columns.map((column, index) => {
                    const columnKey = column;
                    const config = columnConfig[columnKey] || {};
                    const { headerRender, title, hideBorder } = config;
                    const columnTitle = title || column;

                    return (
                      <th
                        key={index}
                        data-testid={headerTestId(columnKey)}
                        className={`${colors.textSubTitle} px-4 ${paddingYHeader} text-left text-xs font-semibold tracking-wider overflow-hidden uppercase relative`}
                        title={!headerRender ? columnTitle : undefined}
                        style={{
                          ...config.style,
                          whiteSpace: config.style?.whiteSpace || "normal",
                          textOverflow: "ellipsis",
                          width: columnWidths[index] ? `${columnWidths[index]}px` : config.style?.width || 'auto',
                        }}
                      >
                        {headerRender ? (
                          headerRender(columnKey)
                        ) : (
                          <div className="truncate py-1" title={columnTitle}>
                            {columnTitle}
                          </div>
                        )}
                        {resizable && (
                          <div
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize`}
                            onMouseDown={(e) => handleResizeStart(e, index)}
                            style={{ userSelect: 'none' }}
                          >
                            {!hideBorder && (index < columns.length - 1 || (index === columns.length - 1 && hasDeleteColumn)) && (
                              <div
                                className={`h-full border-r ${colors.borderPrimary}`}
                                style={{
                                  height: 'calc(100% - 16px)',
                                  margin: '8px 0px',
                                }}
                              ></div>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                  {hasDeleteColumn && (
                    <th
                      className={`${colors.textSubTitle} w-25 p-4 text-center text-xs font-semibold tracking-wider whitespace-nowrap uppercase`}
                      data-testid={headerTestId("action")}
                    >
                      {t("common.action")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className={colors.bgTableBody} data-testid={`${tableTestId}-table-body`}>
                {!loading && data.length === 0 && (
                  <tr data-testid={`${tableTestId}-empty-row`}>
                    <td
                      className={`px-4 py-8 text-center ${colors.textMuted}`}
                      data-testid={noDataTestId}
                      colSpan={
                        columns.length + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0) + (hasDeleteColumn ? 1 : 0)
                      }
                    >
                      {t("common.no-data-to-display")}
                    </td>
                  </tr>
                )}
                {data &&
                  data.length > 0 &&
                  data.map((row, index) => {
                    // If customRowRender is provided, use it
                    if (customRowRender) {
                      return customRowRender(row, index, (
                        <>
                          {hasCheckbox && (
                            <td className={`${showCheckboxOnlyWhenSelected ? 'px-0' : 'px-4'} py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                              <div onClick={(event) => event.stopPropagation()}>
                                <div className="flex items-center justify-center">
                                  {!showCheckboxOnlyWhenSelected || isRowSelected(row) ? (
                                    <>
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={isRowSelected(row)}
                                        disabled={isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))}
                                        onChange={() => handleRowCheckboxChange(row)}
                                      />
                                      <div
                                        className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))
                                          ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgDisabledControl} opacity-50`
                                          : isRowSelected(row)
                                            ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                            : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                          }`}
                                        onClick={() => !isRowDisabled(row) && !(isCheckboxDisabled && isCheckboxDisabled(row)) && handleRowCheckboxChange(row)}
                                      >
                                        {isRowSelected(row) && (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="h-4 w-4"></div>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                          {hasRadioButton && (
                            <td className={`px-4 py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                              <div onClick={(event) => event.stopPropagation()}>
                                <div className="flex items-center justify-center">
                                  <input
                                    type="radio"
                                    className="sr-only"
                                    checked={isRowSelected(row)}
                                    disabled={isRowDisabled(row)}
                                    onChange={() => handleRowRadioButtonChange(row)}
                                  />
                                  <div
                                    className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                      ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                      : isRowSelected(row)
                                        ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                        : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                      }`}
                                    onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                                  >
                                    {isRowSelected(row) && (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <div className={`h-2 w-2 rounded-full ${colors.bgSelectedRadio}`}></div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          )}
                          {columns.map((column, colIndex) => {
                            const columnKey = column;
                            const config = columnConfig[columnKey] || {};
                            const { customRender } = config;

                            return (
                              <td
                                key={colIndex}
                                className={`px-4 py-3 text-sm ${colors.textPrimary} overflow-hidden truncate`}
                                title={
                                  typeof row[columnKey] === "string" || typeof row[columnKey] === "number"
                                    ? row[columnKey]
                                    : undefined
                                }
                                style={{
                                  ...config.style,
                                  whiteSpace: config.style?.whiteSpace || "nowrap",
                                  textOverflow: "ellipsis",
                                  ...(rowHeight && { height: `${rowHeight}px` }),
                                }}
                              >
                                {customRender ? (
                                  <div className="truncate">{customRender(row[columnKey], row, columnKey, index)}</div>
                                ) : (
                                  <div className="truncate">{row[columnKey]}</div>
                                )}
                              </td>
                            );
                          })}
                          {hasDeleteColumn && (
                            <td className="flex items-center justify-center px-4 py-3">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                    onDeleteRow(row);
                                  }
                                }}
                                disabled={isDeleteDisabled?.(row)}
                                className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                                  ? `${colors.textDisabledControl} cursor-not-allowed`
                                  : `${colors.iconDelete} ${colors.hoverTextDanger}`
                                  }`}
                                title={ t("common.delete") }
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </>
                      ));
                    }
                    
                    // Default row rendering
                    return (
                      <tr
                        key={index}
                        data-testid={`${tableTestId}-row-${index}`}
                        className={`${getRowStripeColor(index)} ${isRowSelected(row) ? `!${colors.bgSelectedRow}` : ""} ${isRowDisabled(row) ? "cursor-not-allowed opacity-50" : ""} ${onRowClick && !isRowDisabled(row) ? `cursor-pointer transition-colors ${colors.hoverTableRow}` : ""} ${showCheckboxOnlyWhenSelected && (isCheckboxDisabled && isCheckboxDisabled(row)) ? "" : (isCheckboxDisabled && isCheckboxDisabled(row)) ? "opacity-60" : ""}`}
                        onClick={onRowClick && !isRowDisabled(row) ? () => onRowClick(row, index) : undefined}
                      >
                        {hasCheckbox && (
                          <td className={`${showCheckboxOnlyWhenSelected ? 'px-0' : 'px-4'} py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                {!showCheckboxOnlyWhenSelected || isRowSelected(row) ? (
                                  <>
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={isRowSelected(row)}
                                      disabled={isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))}
                                      onChange={() => handleRowCheckboxChange(row)}
                                    />
                                    <div
                                      className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))
                                        ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgDisabledControl} opacity-50`
                                        : isRowSelected(row)
                                          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                        }`}
                                      onClick={() => !isRowDisabled(row) && !(isCheckboxDisabled && isCheckboxDisabled(row)) && handleRowCheckboxChange(row)}
                                    >
                                      {isRowSelected(row) && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="h-4 w-4"></div>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        {hasRadioButton && (
                          <td className={`px-4 py-3 ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                <input
                                  type="radio"
                                  className="sr-only"
                                  checked={isRowSelected(row)}
                                  disabled={isRowDisabled(row)}
                                  onChange={() => handleRowRadioButtonChange(row)}
                                />
                                <div
                                  className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                    ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                    : isRowSelected(row)
                                      ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                      : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                    }`}
                                  onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                                >
                                  {isRowSelected(row) && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className={`h-2 w-2 rounded-full ${colors.bgActivePrimary}`}></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          const columnKey = column;
                          const config = columnConfig[columnKey] || {};
                          const { customRender } = config;

                          return (
                            <td
                              key={colIndex}
                                className={`px-4 py-3 text-sm ${colors.textPrimary} overflow-hidden truncate`}
                              title={
                                typeof row[columnKey] === "string" || typeof row[columnKey] === "number"
                                  ? row[columnKey]
                                  : undefined
                              }
                              style={{
                                ...config.style,
                                whiteSpace: config.style?.whiteSpace || "nowrap",
                                textOverflow: "ellipsis",
                                ...(rowHeight && { height: `${rowHeight}px` }),
                                width: columnWidths[colIndex] ? `${columnWidths[colIndex]}px` : config.style?.width || 'auto',
                              }}
                            >
                              {customRender ? (
                                <div className="truncate">{customRender(row[columnKey], row, columnKey, index)}</div>
                              ) : (
                                <div className="truncate">{row[columnKey]}</div>
                              )}
                            </td>
                          );
                        })}
                        {hasDeleteColumn && (
                          <td className="flex items-center justify-center px-4 py-3">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                  onDeleteRow(row);
                                }
                              }}
                              disabled={isDeleteDisabled?.(row)}
                              className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                                  ? `${colors.textDisabledControl} cursor-not-allowed`
                                  : `${colors.iconDelete} ${colors.hoverTextDanger}`
                                }`}
                              title={ t("common.delete") }
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination */}
        {showPagination && data.length > 0 && (
          <div className="pt-3" data-testid={`${tableTestId}-pagination`}>
            <div className="flex items-center justify-between gap-4 flex-row">
              {/* Go to page */}
              {shouldShowGoToPage && (
                <div className="flex items-center gap-2 text-sm" data-testid={`${tableTestId}-pagination-controls`}>
                  <span className={`${colors.textSubTitle} text-xs font-medium`}>{t("common.table.go-to-page")}</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInput}
                    data-testid={`${tableTestId}-pagination-go-to-page`}
                    onChange={(e) => {
                      setPageInput(e.target.value);
                    }}
                    onBlur={commitPage}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitPage();
                        e.target.blur();
                      }
                    }}
                    className={`h-8 w-16 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
                  />
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2 text-sm" data-testid={`${tableTestId}-pagination-items-per-page`}>
                    <span className={`${colors.textSubTitle} text-xs font-medium`}>{t("common.table.items-per-page")}</span>
                    <CustomSelect
                      data-testid={`${tableTestId}-pagination-items-per-page-select`}
                      value={{ value: itemsPerPage, label: itemsPerPage.toString() }}
                      onChange={(selectedOption) => {
                        const value = parseInt(selectedOption.value, 10);
                        setItemsPerPage(value);
                        onItemsPerPageChange(value);
                      }}
                      options={[
                        { value: 10, label: "10" },
                        { value: 20, label: "20" },
                        { value: 50, label: "50" },
                      ]}
                      size="sm"
                      isSearchable={false}
                      clearable={false}
                      customStyles={{
                        menu: (base) => ({
                          ...base,
                          width: "60px",
                        })
                      }}
                    />
                  </div>

                  {/* Showing info */}
                  <div className="flex items-center text-sm">
                     <span className={`${colors.textSubTitle} text-xs font-medium`}>
                       {(() => {
                         const startItem = ((currentPage - 1) * itemsPerPage) + 1;
                         const endItem = Math.min(currentPage * itemsPerPage, totalData);
                         const currentLanguage = i18n.language || 'ko';
                         
                         if (currentLanguage === 'ko') {
                           return (
                             <span>
                               총 <span className={colors.textPrimary}>{totalData}</span>개 중 <span className={colors.textPrimary}>{startItem} - {endItem}</span>개 표시 중
                             </span>
                           );
                         } else {
                           return (
                             <span>
                               {t("common.table.showing")} <span className={colors.textPrimary}>{startItem} - {endItem}</span> {t("common.table.of")} <span className={colors.textPrimary}>{totalData}</span>
                             </span>
                           );
                         }
                       })()}
                     </span>
                   </div>
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  {/* Previous Page Button */}
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
                    title="Previous page"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.prev")}</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((page, index) => (
                      <div key={index}>
                        {page === "..." ? (
                          <PaginationEllipsis
                            className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${page === currentPage ? `${colors.bgPaginationButtonActive}` : `${colors.bgInput}`
                              }`}
                          >
                            {page}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Next Page Button */}
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
                    title="Next page"
                  >
                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.next")}</span>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
  }
}

export default TableForProductManagement;
